/**
 * Project WRITE endpoints — request schemas (Pattern B).
 *
 * These schemas ARE THE FIELD WHITELIST for `public.projects`. Both are
 * `.strict()` on purpose: zod silently STRIPS unknown keys otherwise, so a
 * body carrying `organization_id` or `code` would 200 with the key quietly
 * dropped instead of 400ing, and the whitelist would be untestable.
 *
 * Project writes are backend-only once the lockdown migration lands
 * (`*_projects_backend_write_lockdown.sql`: RESTRICTIVE
 * `verify_backend_token()` on INSERT/UPDATE/DELETE, ANDed with the permissive
 * org-membership policies). The handlers in `apps/api/src/routes/projects/projects.ts`
 * write through the per-request user client, which signs `x-backend-auth`.
 * The UPDATE policy has NO `WITH CHECK`, so this schema is the only thing
 * that stops a PATCH from moving a row to another org: `organization_id`,
 * `created_by` and `code` must NEVER be accepted here.
 *
 * DELIBERATELY ABSENT from both schemas:
 *   - `organization_id` — from the validated `:orgId` route param.
 *   - `created_by`      — from the verified token (`c.get("userId")`); the
 *                         INSERT policy's `WITH CHECK` pins it again.
 *   - `code`            — generated server-side (`AAA######`, random suffix,
 *                         23505 retry); immutable after creation.
 *   - `team_size`       — stale column, superseded by the view's team count.
 * `image_*` are accepted by UPDATE only (the image upload/remove path writes
 * them after the storage upload succeeds).
 *
 * Two references are checked in the handler, not here: `project_type_id` must
 * be a type visible to the org (the FK ignores RLS), and `artistic_director_id`
 * must be a `people` row in the org (FK to `people`, ON DELETE SET NULL; the
 * same-org guard trigger `projects_validate_artistic_director` backs it up).
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
/**
 * The `projects_status_check` CHECK's values, in lifecycle order. NULL is
 * also allowed by the CHECK (and is the "No status" filter option), so the
 * schemas below make status nullable. `on_hold` is NOT a status — the legacy
 * form offered it and the DB rejected it.
 */
export const PROJECT_STATUSES = [
    "planning",
    "active",
    "paused",
    "completed",
    "cancelled",
];
export const projectStatusSchema = z.enum(PROJECT_STATUSES);
/** The `projects_carbon_footprint_level_check` CHECK's values. */
export const CARBON_FOOTPRINT_LEVELS = ["low", "medium", "high", "net_zero"];
export const carbonFootprintLevelSchema = z.enum(CARBON_FOOTPRINT_LEVELS);
const nullableDate = calendarDateSchema.nullable().optional();
/** Fields a create and an update share (everything but `name` / `project_type_id`). */
const projectOptionalFields = {
    description: z.string().max(10000).nullable().optional(),
    status: projectStatusSchema.nullable().optional(),
    start_date: nullableDate,
    end_date: nullableDate,
    planning_start_date: nullableDate,
    preproduction_start_date: nullableDate,
    postevent_end_date: nullableDate,
    /** `projects_budget_non_negative`. */
    budget: z.number().finite().min(0).nullable().optional(),
    /** A `people` id — org membership is checked in the handler. */
    artistic_director_id: z.string().uuid().nullable().optional(),
    /** `projects_sustainability_score_range`. */
    sustainability_score: z.number().int().min(0).max(100).nullable().optional(),
    carbon_footprint_level: carbonFootprintLevelSchema.nullable().optional(),
    /** `projects_local_sourcing_percentage_check`. */
    local_sourcing_percentage: z.number().finite().min(0).max(100).nullable().optional(),
    sustainability_notes: z.string().max(10000).nullable().optional(),
};
/**
 * POST /orgs/:orgId/projects body. `name` and `project_type_id` are the two
 * NOT NULL columns without a default the caller owns.
 */
export const createProjectSchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    project_type_id: z.string().uuid(),
    ...projectOptionalFields,
})
    .strict();
/**
 * PATCH /orgs/:orgId/projects/:projectId body — any subset, at least one key.
 * `name` and `project_type_id` stay non-nullable (NOT NULL columns). The three
 * image URLs are nullable so the remove-image path can clear them.
 */
export const updateProjectSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    project_type_id: z.string().uuid().optional(),
    ...projectOptionalFields,
    image_small: z.string().nullable().optional(),
    image_medium: z.string().nullable().optional(),
    image_large: z.string().nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
});
//# sourceMappingURL=projects.js.map